import React, { useState, useEffect, useCallback, useContext } from 'react';
import { UserContext } from '../../App';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const API_URL = process.env.REACT_APP_API_URL;

// --- Inline SVG Icons ---
const iconCheck = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
);
const iconLoading = (
    <svg className="animate-spin h-5 w-5 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
);
const iconSearch = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
    </svg>
);

const DeliveryPage = () => {
    const { user } = useContext(UserContext);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusMessage, setStatusMessage] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [deliveryStats, setDeliveryStats] = useState({});
    const accessToken = localStorage.getItem("accessToken");

    const showMessage = useCallback((message) => {
        setStatusMessage(message);
        setTimeout(() => setStatusMessage(null), 5000);
    }, []);

    const fetchMyDeliveries = useCallback(async () => {
        setLoading(true);
        if (!user || !user.userId) {
            setLoading(false);
            showMessage("Erreur: Impossible de trouver votre ID. Veuillez vous reconnecter.");
            return;
        }

        try {
            const queryParams = new URLSearchParams();
            if (searchTerm) queryParams.append('q', searchTerm);
            if (filterStatus !== 'all') queryParams.append('status', filterStatus);

            const res = await fetch(`${API_URL}/delivery-person/${user.userId}/orders?${queryParams.toString()}`, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });

            if (!res.ok) throw new Error("Échec de la récupération de vos livraisons.");

            const data = await res.json();
            setOrders(data.orders);
            console.log('items', data)

            const stats = data.orders.reduce((acc, order) => {
                const status = order.status;
                acc[status] = (acc[status] || 0) + 1;
                return acc;
            }, { pending: 0, in_transit: 0, delivered: 0, cancelled: 0 });
            setDeliveryStats(stats);
        } catch (err) {
            console.error("Erreur de l'API:", err);
            showMessage("Une erreur est survenue lors du chargement de vos livraisons.");
        } finally {
            setLoading(false);
        }
    }, [user, accessToken, showMessage, searchTerm, filterStatus]);

     useEffect(() => {
         setSearchTerm('');
         fetchMyDeliveries();
     }, [user, accessToken, showMessage, filterStatus, fetchMyDeliveries]);
 
     const handleSearchSubmit = (e) => {
         e.preventDefault();
         fetchMyDeliveries(); 
     };

    const handleUpdateOrderStatus = async (orderId, newStatus) => {
        try {
            const res = await fetch(`${API_URL}/orders/${orderId}/status`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({ status: newStatus })
            });

            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.message || `Échec de la mise à jour du statut de la commande #${orderId}.`);
            }

            await res.json();
            fetchMyDeliveries();
            showMessage(`Statut de la commande #${orderId} mis à jour avec succès.`);
        } catch (err) {
            console.error(err);
            showMessage(err.message || "Une erreur est survenue lors de la mise à jour.");
        }
    };

    const statusMap = {
        'all': 'Toutes les commandes',
        'pending': 'En attente',
        'in_transit': 'En cours de livraison',
        'delivered': 'Livrée',
        'cancelled': 'Annulée'
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'in_transit': return 'bg-blue-100 text-blue-800';
            case 'delivered': return 'bg-green-100 text-green-800';
            case 'cancelled': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const chartData = Object.keys(deliveryStats).map(key => ({
        name: statusMap[key],
        count: deliveryStats[key]
    }));
    

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-2xl text-gray-700 animate-pulse flex items-center space-x-2">
                    {iconLoading}
                    <span>Chargement de vos livraisons...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen font-sans p-8 text-gray-900">
            <div className="max-w-8xl mx-auto">
                {/* Header Section */}
                <header className="mb-10">
                    <h1 className="text-4xl font-extrabold text-gray-900 flex items-center gap-4">
                        <span className="text-indigo-600">🚚</span>
                        Mes Livraisons
                    </h1>
                    <p className="mt-2 text-lg text-gray-500">
                        Gérez vos livraisons, suivez leur statut et optimisez vos trajets.
                    </p>
                </header>

                {/* Status Message */}
                {statusMessage && (
                    <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6 rounded-lg shadow-sm animate-fade-in" role="alert">
                        <p className="font-bold">Succès</p>
                        <p>{statusMessage}</p>
                    </div>
                )}

                {/* Dashboard & Filters Section */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                    {/* Status Dashboard Card */}
                    <div className="bg-white p-6 rounded-2xl shadow-xl lg:col-span-2">
                        <h2 className="text-2xl font-bold mb-4 text-gray-800">Aperçu du statut</h2>
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="name" />
                                <YAxis allowDecimals={false} />
                                <Tooltip cursor={{ fill: 'rgba(0,0,0,0.05)' }} />
                                <Bar dataKey="count" name="Nombre de commandes" barSize={40} fill="#4f46e5" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Search & Filter Card */}
                    <div className="bg-white p-6 rounded-2xl shadow-xl flex flex-col gap-4">
                        <h2 className="text-2xl font-bold text-gray-800">Rechercher et filtrer</h2>

                        <form className="relative flex items-center" onSubmit={handleSearchSubmit}>
                            <span className="absolute left-3 text-gray-400">{iconSearch}</span>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Rechercher par nom, email ou adresse du client..."
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                        </form>


                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            {Object.keys(statusMap).map(key => (
                                <option key={key} value={key}>{statusMap[key]}</option>
                            ))}
                        </select>
                        <button
                            onClick={fetchMyDeliveries}
                            className="w-full px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-colors"
                        >
                            Appliquer les filtres
                        </button>
                    </div>
                </div>

                {/* Orders List Section */}
                {orders.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl shadow-xl">
                        <svg className="w-24 h-24 text-gray-300 mb-6" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm0 2h12v10H4V5z" />
                            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3a1 1 0 11-2 0V6a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                        <h2 className="text-2xl font-bold text-gray-700 mb-2">Aucune livraison trouvée</h2>
                        <p className="text-gray-500 text-lg">Vérifiez vos filtres de recherche ou revenez plus tard.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {orders.map(order => (
                            <div key={order.orderId} className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl transition-shadow duration-300">
                                {/* Order Header */}
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-bold text-gray-800">Commande #{order.orderId}</h3>
                                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusStyle(order.status)}`}>
                                        {statusMap[order.status]}
                                    </span>
                                </div>

                                {/* Client Info */}
                                <div className="space-y-2 text-sm text-gray-600">
                                    <p className="truncate"><span className="font-semibold text-gray-800">Client:</span> {order.customerFirstName} {order.customerLastName}</p>
                                    <p className="truncate"><span className="font-semibold text-gray-800">Adresse:</span> {order.deliveryAddress}</p>
                                    <p><span className="font-semibold text-gray-800">Téléphone:</span> {order.customerPhoneNumber}</p>
                                </div>

                                {/* Order Items */}
                                <div className="border-t border-gray-200 pt-4 mb-4">
                                    <h4 className="font-semibold text-lg text-gray-700 mb-2">Détails de la commande</h4>
                                    <ul className="list-disc list-inside space-y-1 text-gray-600">
                                        {order.orderItems.map((item, index) => (
                                            <li key={index}>
                                                <span className="font-medium">{item.quantity} x {item.productName}</span>
                                                <span className="text-sm"> ({parseFloat(item.priceAtTimeOfOrder).toFixed(2)} CDF/{item.unit}) : <span className="font-semibold text-sm text-gray-700">{item.supplierLastName}</span> </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Total */}
                                <div className="border-t border-gray-200 pt-4 mb-6 flex justify-between items-center">
                                    <p className="text-xl font-bold text-red-700">Total: {parseFloat(order.totalAmount).toFixed(2)} CDF</p>
                                    <p className="text-sm text-gray-500">Commandé le: {new Date(order.createdAt).toLocaleDateString('fr-FR')}</p>
                                </div>

                                <div className="border-t border-dashed border-gray-200 my-4"></div>


                                {order.status === 'in_transit' && (
                                    <button
                                        onClick={() => handleUpdateOrderStatus(order.orderId, 'delivered')}
                                        className="w-full px-4 py-2 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
                                    >
                                        {iconCheck}
                                        Marquer comme livrée
                                    </button>
                                )}

                                {order.status === 'pending' && (
                                    <button
                                        onClick={() => handleUpdateOrderStatus(order.orderId, 'in_transit')}
                                        className="w-full px-4 py-2 bg-indigo-500 text-white font-semibold rounded-lg hover:bg-indigo-600 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M20 18h-2V7H4V3H2v15a2 2 0 002 2h16a2 2 0 002-2v-2zM8 12v-2h2v2H8zm4 0v-2h2v2h-2z" />
                                        </svg>
                                        Commencer la livraison
                                    </button>
                                )}


                                <div className="mt-4 rounded-xl overflow-hidden shadow-md">
                                    <iframe
                                        width="100%"
                                        height="200"
                                        frameBorder="0"
                                        scrolling="no"
                                        marginHeight="0"
                                        marginWidth="0"
                                        src={`https://maps.google.com/maps?q=${encodeURIComponent(order.deliveryAddress)}&t=&z=13&ie=UTF8&iwloc=&output=embed`}
                                        title={`Map of ${order.deliveryAddress}`}
                                    ></iframe>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DeliveryPage;